define((require) => {
    "use strict";

    const ImageURLProvider      = require("shared/ui/experience/widgets/imageWidget/ImageURLProvider");
    const Marionette            = require("marionette");
    const Size                  = require("shared/util/Size");
    const WidgetCollection      = require("shared/ui/experience/widgets/WidgetCollection");
    const WidgetTypes           = require("shared/ui/experience/widgets/ViewerWidgetTypes");

    // Class added to any Scene via "hide()" not removed until "show()" is called, which is
    // only when the Scene is actually ready to be shown to the end-user.
    const INVISIBLE_CLASS = "invisible";

    /**
     * Viewer for a single scene.
     *
     * @author Yijie Li
     * @class SceneViewer
     * @constructor
     *
     * @cfg {Scene} model The scene model.
     * @cfg {ExperienceViewModel|SceneThumbnailViewModel} viewModel The view model for the view containing the scene.
     * @cfg {Object} [maxDimensions] Optional max dimensions to scale the viewer to. If specified, must contain width, height, or both.
     * @cfg {Boolean} [enableAnimations] If true, animations will be enabled on the scene.
     * @cfg {Boolean} [extractCollection] Optional, advanced option used for manual handing of the scenes collection. Defaults to true.
     * @cfg {Boolean} [disableScale] If true, the scene will not be scaled to fit its container.
     */
    return Marionette.CompositeView.extend({ // Not ImprovedView, since this class is used in the viewer

        className: "scene-viewer",

        template: () => (""),

        options: {
            extractCollection: true
        },

        viewScope: {
            getSceneNumber() {
                return this.model.get("sceneNumber");
            }
        },

        /**
         * @private
         * First rendering always inactive by default, the current Scene will then trigger a re-render when
         * becoming active. This ensures that all other Scenes are inactive by default - as they should be -
         * when rendered the first time (as an adjacent Scene).
         */
        _active: false,

        /**
         * @type {ExperienceModel}
         */
        _experience: null,

        /**
         * The backdrop container element, if any...
         *
         * @type {jQueryHTMLElement}
         */
        _backdropContainer: null,

        modelEvents: {
            // Listen for scale/resize changes in order to update scene size
            "change:scale": "_scaleChangeHandler"
        },

        itemViewOptions() {
            return {
                viewModel:        this.options.viewModel,
                enableAnimations: this.options.enableAnimations,
                enableActions:    this.options.enableActions || this.options.enableAnimations
            };
        },

        getItemView(item) {
            return WidgetTypes.lookup(item.get("type")).viewClass;
        },

        widgetViewFromModel(model) {
            return this.children.findByModel(model);
        },

        initialize() {
            this._experience = this.options.viewModel.get("experience");

            if (this.options.extractCollection) {
                this.collection = this.model.get("widgets");
            } else {
                this.collection = new WidgetCollection();
                this.listenTo(this.collection, "add", this._updateToActiveState);
            }

            this.listenTo(this, "show", this._renderBackdrop);
        },

        /**
         * Ensures the Scene controlled by this Scene Viewer is no longer invisible to the end-user and
         * potentially ready for interaction, like the user clicking an Action on a Widget in the Scene.
         *
         * @return {SceneViewer} This scene viewer; never falsey.
         * @see    #hide
         */
        show() {
            this.$el.removeClass(INVISIBLE_CLASS);
            return this;
        },

        /**
         * Ensures the Scene controlled by this Scene Viewer is invisible to the end-user and not
         * ready for interaction, but always present in the dom (to avoid all kinds of nasty issues
         * with dom elements not displayed in the dom, e.g. issues with embedding Experiences on
         * adjacent Scenes currently not shown).
         *
         * @return {SceneViewer} This scene viewer; never falsey.
         * @see    #show
         */
        hide() {
            this.$el.addClass(INVISIBLE_CLASS);
            return this;
        },

        /**
         * Computes the size to view the experience.
         *
         * @returns {{width: *, height: *}}
         */
        getSize() {
            const scaledDimensions = this._experience.getDimensions(this.options.maxDimensions);

            scaledDimensions.scaledWidth = scaledDimensions.width;
            scaledDimensions.scaledHeight = scaledDimensions.height;
            scaledDimensions.width = this._experience.get("width");
            scaledDimensions.height = this._experience.get("height");

            return scaledDimensions;
        },

        /**
         * Set the active state of the scene, i.e. any contained widgets will become
         * active, starting animations etc.
         *
         * @param {Boolean} value Whether the scene is considered active or not.
         */
        setActive(value) {
            this._active = value;
            this._updateToActiveState();
        },

        isActive() {
            return this._active;
        },

        updateToVisibilityState() {
            this._updateToActiveState();
        },

        /**
         * Invoked when the scene model's scale changes.
         */
        _scaleChangeHandler() {
            this._updateSize();
        },

        /**
         * Computes the rect for the specified widget view. Default implementation delegates to <tt>AbstractWidgetView#getBoundingBox</tt>.
         *
         * May be overridden by subclasses.
         *
         * @param  {AbstractWidgetView} widget The widget view.
         * @return {Rectangle} a rectangle representing the size and position of the widget.
         */
        _calculateWidgetRect(widget) {
            return widget.getBoundingBox();
        },

        /**
         * @private
         *
         * Delegates the active state to any contained widgets, if we're not in a pending state.
         *
         * @see  #onRender
         */
        _updateToActiveState() {
            if (!this._active) {
                this.children.forEach((widget) => {
                    widget.setActive(false);
                });

                return;
            }

            const visiblePartOfExperience = this.options.viewModel.get("viewportVisibility");

            this.children.forEach((widget) => {
                const widgetRectangle = this._calculateWidgetRect(widget);
                const widgetIsVisible = visiblePartOfExperience.intersects(widgetRectangle);

                widget.setActive(widgetIsVisible);
            });
        },

        /**
         * @private
         *
         * Sets the size of the scene element, equal to the scaled size of the current experience.
         *
         * @see  ExperienceModel.scale
         */
        _updateSize() {
            this.$el.css({
                width: this._experience.get("scaledWidth"),
                height: this._experience.get("scaledHeight")
            });
        },

        /**
         * @private
         *
         * Applies the size and background color to this scene instance.
         *
         * If the scene is in a pending state, the scene is hidden until all widgets have been
         * successfully loaded.
         */
        onRender() {
            const css = {};

            if (this.options.maxDimensions) {
                const scale = this.getSize().scale;
                css.transform = `scale(${scale}, ${scale})`;
            }

            this._updateSize();
            this._updateToActiveState();

            this.$el.css(css);
        },

        onClose() {
            // Clean up any backdrop leftovers...
            if (this._backdropContainer) {
                this._backdropContainer
                    .removeClass("fit-container")
                    .removeClass("responsive-backdrop")
                    .css({
                        backgroundImage: "",
                        backgroundColor: ""
                    });
            }
        },

        /**
         * Renders the experience backdrop, either an image or color.
         *
         * @private
         */
        _renderBackdrop() {
            if (!this.model.get("isBackgroundScene")) {
                return;
            }

            const backdrop = this._experience.get("backdrop");
            const container = backdrop.isFitContainer() ? this.$el.closest(".experience-container") : this.$el;


            if (backdrop.isFitContainer()) {
                container.addClass("fit-container");
            }

            if (backdrop.isTypeImage() && backdrop.has("image")) {
                const image = backdrop.get("image");
                // Note this is called on show, we should be able to get the bounds by jQuery as below. But for views
                // that does not compliant to the backbone render cycle, for example not calling region.show in onShow,
                // the bounds will be {0, 0} here. We probably need another task to solve that issue.
                // If we get the bounds, we need to take into account the ratio in order to get the appropriate size.
                // Otherwise, we may get a smaller image, which will be scaled up and appears blurry.
                const size = new Size(container[0].offsetWidth, container[0].offsetHeight);
                const bounds = size.growToAspectRatio(image.width / image.height);
                const url = ImageURLProvider.provide(backdrop.getMockModel(), {bounds}) || "";

                container.css("background-image", `url(${url})`).addClass("responsive-backdrop");
            } else {
                container.css("background-color", backdrop.getColor());
            }

            // Keep a reference to the backdrop container in order to do proper cleanup later - see #onClose...
            this._backdropContainer = container;
        },

        /**
         * Starts (or re-starts) the animations of all widgets on the scene.
         */
        startAnimations() {
            this.children.invoke("startAnimations");
        },

        /**
         * Stops and resets the animations of all widgets on the scene.
         */
        stopAnimations() {
            this.children.invoke("stopAnimations");
        }
    });
});



// WEBPACK FOOTER //
// ./src/shared/ui/viewer/SceneViewer.js